import React, { useState, useEffect } from "react";
import { FaMagic, FaLightbulb, FaParagraph, FaStream, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import MonoButton from "../kits/DesignKit/MonoButton";
import { useEditorSettings } from "../hooks/useEditorSettings";
import {
  checkOllamaStatus,
  improveText,
  continueText,
  summarizeText,
  suggestMarkdownFormatting
} from "../services/ollamaService";

/**
 * AI Panel for ZenEditor
 * Provides AI-powered features using Ollama
 */
function ZenEditorAIPanel({ selectedText, onInsertText, onReplaceText }) {
  const { settings } = useEditorSettings();
  const [ollamaStatus, setOllamaStatus] = useState({ available: false, models: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState("");
  const [activeFeature, setActiveFeature] = useState(null);

  // Check Ollama status on mount
  useEffect(() => {
    const checkStatus = async () => {
      const status = await checkOllamaStatus();
      setOllamaStatus(status);
      if (!status.available) {
        setError("Ollama ist nicht erreichbar. Stelle sicher, dass Ollama läuft.");
      }
    };
    checkStatus();
  }, []);

  const handleAIFeature = async (feature, aiFunction) => {
    if (!selectedText || selectedText.trim() === "") {
      setError("Bitte wähle zuerst Text im Editor aus.");
      return;
    }

    if (!ollamaStatus.available) {
      setError("Ollama ist nicht verfügbar. Starte Ollama oder konfiguriere die API-URL.");
      return;
    }

    setLoading(true);
    setError("");
    setResult("");
    setActiveFeature(feature);

    try {
      // Use settings for AI model
      const response = await aiFunction(selectedText, settings.aiModel);

      if (response.success) {
        setResult(response.text);
      } else {
        setError(response.error || "Fehler bei der AI-Verarbeitung");
      }
    } catch (err) {
      setError(err.message || "Unerwarteter Fehler");
    } finally {
      setLoading(false);
    }
  };

  const handleUseResult = () => {
    if (result) {
      onReplaceText(result);
      setResult("");
      setActiveFeature(null);
    }
  };

  const handleInsertResult = () => {
    if (result) {
      onInsertText(result);
      setResult("");
      setActiveFeature(null);
    }
  };

  const handleCancel = () => {
    setResult("");
    setError("");
    setActiveFeature(null);
  };

  return (
    <div className="bg-white border border-gray-300 rounded-lg p-4">
      {/* Status Indicator */}
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs">
          {ollamaStatus.available ? (
            <>
              <FaCheckCircle className="text-green-500" />
              <span className="text-green-700 font-semibold">Ollama verbunden</span>
              {ollamaStatus.models.length > 0 && (
                <span className="text-gray-600">({ollamaStatus.models.length} Modelle)</span>
              )}
            </>
          ) : (
            <>
              <FaExclamationTriangle className="text-orange-500" />
              <span className="text-orange-700 font-semibold">Ollama nicht verfügbar</span>
            </>
          )}
        </div>
        <div className="text-xs text-gray-600">
          Model: <span className="font-mono font-semibold text-brand">{settings.aiModel}</span>
        </div>
      </div>

      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <FaMagic className="text-brand" />
        AI-Features
      </h3>

      {/* AI Action Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
        <MonoButton
          onClick={() => handleAIFeature("improve", improveText)}
          disabled={loading || !ollamaStatus.available}
          className="text-xs px-3 py-2 flex items-center gap-2 justify-center"
        >
          <FaLightbulb />
          Text verbessern
        </MonoButton>

        <MonoButton
          onClick={() => handleAIFeature("continue", continueText)}
          disabled={loading || !ollamaStatus.available}
          className="text-xs px-3 py-2 flex items-center gap-2 justify-center"
        >
          <FaStream />
          Text fortsetzen
        </MonoButton>

        <MonoButton
          onClick={() => handleAIFeature("summarize", summarizeText)}
          disabled={loading || !ollamaStatus.available}
          className="text-xs px-3 py-2 flex items-center gap-2 justify-center"
        >
          <FaParagraph />
          Zusammenfassen
        </MonoButton>

        <MonoButton
          onClick={() => handleAIFeature("format", suggestMarkdownFormatting)}
          disabled={loading || !ollamaStatus.available}
          className="text-xs px-3 py-2 flex items-center gap-2 justify-center"
        >
          <FaMagic />
          Markdown-Format
        </MonoButton>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand"></div>
            <span className="text-xs text-blue-700">
              {activeFeature === "improve" && "Verbessere Text..."}
              {activeFeature === "continue" && "Generiere Fortsetzung..."}
              {activeFeature === "summarize" && "Erstelle Zusammenfassung..."}
              {activeFeature === "format" && "Formatiere Markdown..."}
            </span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {/* Result Display */}
      {result && (
        <div className="bg-green-50 border border-green-200 rounded p-4 mb-4">
          <div className="mb-2 flex items-center gap-2">
            <FaCheckCircle className="text-green-500" />
            <span className="text-xs font-semibold text-green-700">AI-Ergebnis:</span>
          </div>
          <div className="bg-white border border-gray-200 rounded p-3 mb-3 max-h-48 overflow-y-auto">
            <pre className="text-xs whitespace-pre-wrap font-mono text-gray-800">{result}</pre>
          </div>
          <div className="flex gap-2 flex-wrap">
            <MonoButton
              onClick={handleUseResult}
              className="text-xs px-3 py-1 flex items-center gap-2"
            >
              <FaCheckCircle />
              Ersetzen
            </MonoButton>
            <MonoButton
              onClick={handleInsertResult}
              className="text-xs px-3 py-1 flex items-center gap-2"
            >
              <FaStream />
              Einfügen
            </MonoButton>
            <button
              onClick={handleCancel}
              className="text-xs px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Info Box */}
      {!ollamaStatus.available && (
        <div className="bg-gray-50 border border-gray-200 rounded p-3 mt-4">
          <p className="text-xs text-gray-700 mb-2">
            <strong>Ollama einrichten:</strong>
          </p>
          <ol className="text-xs text-gray-600 space-y-1 list-decimal ml-4">
            <li>Installiere Ollama: <code className="bg-[#d9d4c5] px-1 rounded">curl -fsSL https://ollama.com/install.sh | sh</code></li>
            <li>Starte ein Modell: <code className="bg-[#d9d4c5] px-1 rounded">ollama run llama3.2</code></li>
            <li>Lade die Seite neu</li>
          </ol>
        </div>
      )}

      {/* Selected Text Info */}
      {selectedText && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-600 mb-1">Ausgewählter Text:</p>
          <div className="bg-gray-50 border border-gray-200 rounded p-2 max-h-24 overflow-y-auto">
            <p className="text-xs text-gray-700 truncate">
              {selectedText.substring(0, 150)}
              {selectedText.length > 150 && "..."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ZenEditorAIPanel;
